import api from './api';
import type { AreaRegion } from '@app-types/models/area';
import type { BusinessProfile } from '@app-types/models/business';
import type { Stylist } from '@app-types/models/stylist';
import type { Campaign } from '@app-types/models/user';

interface StaffStatusParams {
  staffId: string;
  status: string;
}

interface BusinessStatusPayload {
  status: string;
  [key: string]: unknown;
}

interface LastListingDateResponse {
  lastListingDate: string | null;
}

interface WeeklyShowcaseCountResponse {
  weeksPassed: number;
  currentWeekCount: number;
}


export const getAreas = async (): Promise<AreaRegion[]> => {
  try {
    const response = await api.get<AreaRegion[]>('/business/areas');
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching areas:', error);
    throw error;
  }
};

export const getBusinessProfile = async (): Promise<BusinessProfile> => {
  try {
    const response = await api.get<BusinessProfile>('/business/profile');
    console.log('Fetched business profile:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching business profile:', error);
    throw error;
  }
};

export const getStaffProfile = async (): Promise<Stylist> => {
  try {
    const response = await api.get<Stylist>(`/business/staff`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching staff profile:', error);
    throw error;
  }
};

export const updateBusinessProfile = async (profileData: Partial<BusinessProfile>): Promise<BusinessProfile> => {
  try {
    const response = await api.put<BusinessProfile>('/business/profile', profileData);
    console.log('Updated business profile:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error updating business profile:', error);
    throw error;
  }
};

export const updateBusinessStatus = async (status: BusinessStatusPayload): Promise<BusinessProfile> => {
  try {
    const response = await api.put<BusinessProfile>('/business/status', status);
    console.log('Updated business status:', response.data);
    return response.data;
  } catch (error: unknown) {
    console.error('Error updating business status:', error);
    throw error;
  }
};

export const updateStaffStatus = async ({staffId, status}: StaffStatusParams): Promise<Stylist> => {
  try {
    const response = await api.put<Stylist>('/business/staff/status', {staffId, status});
    return response.data;
  } catch (error: unknown) {
    console.error('Error updating staff status:', error);
    throw error;
  }
};

export const getLastListingDate = async (): Promise<LastListingDateResponse> => {
  try {
    const response = await api.get<LastListingDateResponse>(`/business/staff/last-listing-date`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching last listing date:', error);
    throw error;
  }
};

export const getStylistShowcaseCount = async (stylistId: string): Promise<number> => {
  try {
    const response = await api.get<number>(`/business/showcase-count/${stylistId}`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching stylist showcase count:', error);
    throw error;
  }
};

export const getWeeklyShowcaseCount = async (): Promise<WeeklyShowcaseCountResponse> => {
  try {
    const response = await api.get<WeeklyShowcaseCountResponse>(`/business/weekly-showcase-counts`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching weekly showcase count:', error);
    throw error;
  }
};

export const getCampaigns = async (): Promise<Campaign[]> => {
  try {
    const response = await api.get<Campaign[]>(`/business/campaigns`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

export const getLanguage = async (): Promise<string | undefined> => {
  try {
    const response = await api.get<string | undefined>(`/business/language`);
    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching language:', error);
    throw error;
  }
};

export const updateLanguage = async (languageCode: string): Promise<unknown> => {
  try {
    const response = await api.put<unknown>('/business/language', {languageCode});
    return response.data;
  } catch (error: unknown) {
    console.error('Error updating language:', error);
    throw error;
  }
};

export const updateUserFlag = async (flagName: string, flagValue: boolean): Promise<{ flagName: string; flagValue: boolean }> => {
  try {
    const response = await api.patch<{ flagName: string; flagValue: boolean }>('/business/flag', { flagName, flagValue });
    console.log(`Updated flag ${flagName} to ${flagValue}:`, response.data);
    return response.data;
  } catch (error: unknown) {
    console.error(`Error updating flag ${flagName}:`, error);
    throw error;
  }
};
